import { useState } from 'react';
import styled from 'styled-components';

import { Tag } from '../../cdl/Tag/Tag';
import { Text } from '../../cdl/Text/Text';
import { TextButton } from '../../common/buttons/TextButton';
import { formatMoney } from '../../common/helpers/formatMoney.helper';
import { translate } from '../../common/helpers/translate.helper';
import { PortDirectory } from '../../common/port-directory/PortDirectory';
import { Triangle } from '../../common/Triangle';
import { Box } from '../../common/ui/Box';
import { formatNumber } from '../../common/helpers/formatNumber.helper';
import { formatPrice } from '../../common/helpers/formatPrice.helper';

import { ItemsPopup } from './ItemsPopup';
import { OutdatedPriceTag } from '../create-enquiry/components/OutdatedPriceTag';

const StyledTriangle = styled(Triangle)`
    vertical-align: middle;
    margin-right: 5px;
    position: relative;
    top: -1px;
`;

const Price = ({ price, tag }) => {
    const getColorForPrice = (price) => {
        if (price.bestPrice) {
            return 'positive.foreground';
        }

        if (price.worstPrice) {
            return 'negative.foreground';
        }

        return 'foreground.default';
    };

    const hasPartialPricing = price.partialTotalPrice && !price.totalPrice;
    const hasNoPricing = !price.partialTotalPrice && !price.totalPrice;

    const worstPriceDifference = price.worstPriceDifference;
    const formattedWorstPriceDifference = worstPriceDifference
        ? formatMoney({
              value: Math.abs(worstPriceDifference.value),
              currency: worstPriceDifference.currency,
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
          })
        : null;

    const formattedTotalPrice = price.totalPrice
        ? formatMoney({
              value: price.totalPrice.value,
              currency: price.totalPrice.currency,
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
          })
        : null;

    if (hasNoPricing) {
        return (
            <Text size="small" color="foreground.subtle" marginBottom="18px">
                -
            </Text>
        );
    }

    if (hasPartialPricing) {
        return (
            <Text size="small" color="foreground.subtle" marginBottom="18px">
                {translate('compare.partialPricing')}
            </Text>
        );
    }

    if (!worstPriceDifference) {
        return (
            <Box display="flex" alignItems="center" gap={8} marginBottom="18px">
                <Text size={24} color={getColorForPrice(price)}>
                    {formattedTotalPrice}
                </Text>
                {tag}
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={8}>
                <Text size={24} color={getColorForPrice(price)}>
                    {formattedTotalPrice}
                </Text>
                {tag}
            </Box>

            <Text size={14} color="positive.emphasis" display="block">
                <StyledTriangle direction="down" />
                {formattedWorstPriceDifference}
            </Text>
        </Box>
    );
};

export const PriceBoxContent = ({ price, contractName, isDefaultContract }) => {
    const [popupOpen, setPopupOpen] = useState(false);

    const onPopupButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        setPopupOpen(true);
    };

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box>
                <Box display="flex" alignItems="center" maxWidth="100%">
                    <Text size={16} weight="semi">
                        {price.supplier.supplierGroup.name}
                    </Text>
                </Box>

                {contractName ? (
                    <Box display="flex" alignItems="center" maxWidth="100%">
                        <Text
                            variant="extraSmall"
                            color="foreground.muted"
                            display="block"
                            style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {contractName}
                        </Text>

                        {isDefaultContract ? (
                            <Box marginLeft={4} style={{ flexShrink: 0 }}>
                                <Tag variant="default">{translate('compare.defaultContract')}</Tag>
                            </Box>
                        ) : null}
                    </Box>
                ) : null}

                <Box marginTop={6}>
                    <Price
                        price={price}
                        tag={price.isOutdated ? <OutdatedPriceTag validUntil={price.validUntil} /> : null}
                    />
                </Box>
            </Box>

            <Box marginTop="auto" paddingBottom={16}>
                <Box display="flex" marginBottom={4}>
                    {price.ppl ? (
                        <Text size={14} color="foreground.subtle">
                            {translate('compare.price', {
                                ppl: formatPrice(price.ppl),
                            })}
                        </Text>
                    ) : null}

                    <Text size={14} color="foreground.subtle" marginLeft={price.ppl ? 4 : 0}>
                        {translate('compare.volume', {
                            volume: formatNumber({
                                number: price.volume,
                            }),
                        })}
                        L
                    </Text>
                </Box>

                <Box marginBottom={4}>
                    <TextButton onClick={onPopupButtonClick}>{translate('compare.itemsPopup.button')}</TextButton>
                </Box>

                <Box>
                    <PortDirectory portId={price.portId} supplierGroupId={price.supplier.supplierGroup.id} />
                </Box>
            </Box>

            <ItemsPopup price={price} onDismiss={() => setPopupOpen(false)} open={popupOpen} />
        </Box>
    );
};
